import React, { useRef, useEffect, useMemo, useState } from 'react';
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import "../styles/map.css";
import { useSelector } from 'react-redux';
import { formatTimestamp, formatDuration } from '../lib/formatUtils';
import { WeldPassList } from './WeldPassList';

// Moving this to .env throws error
// mapboxgl.accessToken = "xyz";
mapboxgl.accessToken =
  "pk.eyJ1IjoibWFuanVuYXRoLWxvZ3ljZW50IiwiYSI6ImNseWd1NGl3ODBibGwybHNhdmFnNjN2YnAifQ._3p7J3DCBjxNHUFDxNQQqQ";

const MapView = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [is3DView, setIs3DView] = useState(false);
  
  // State for weld details modal
  const [isWeldDetailsModalOpen, setIsWeldDetailsModalOpen] = useState(false);
  const [selectedWeldData, setSelectedWeldData] = useState<any>(null);
  
  // ONLY read from existing Redux store - NO API calls, NO actions
  const weldData = useSelector((state: any) => state.weldData?.weldData || []);
  const loading = useSelector((state: any) => state.weldData?.loading || false);
  const error = useSelector((state: any) => state.weldData?.error || null);
  const currentProject = useSelector((state: any) => state.weldData?.currentProject || null);
  const statusCalculationMethod = useSelector((state: any) => state.weldData?.statusCalculationMethod || 'Instantaneous');

  // Function to handle opening weld passes modal
  const handleOpenWeldPasses = (weld: any) => {
    console.log('🔍 MapView - handleOpenWeldPasses called with weld:', weld);
    
    // Transform weld data to match WeldData interface expected by WeldPassList
    const weldData = {
      id: weld.id,
      weldNumber: weld.weldNumber,
      weldIdentifier: weld.weldIdentifier || weld.weldNumber,
      wpsNumber: weld.wpsNumber || '',
      status: weld.status || 'Not Started',
      weldTime: weld.weldTime || '-',
      startTime: weld.startTime,
      endTime: weld.endTime,
      projectId: weld.projectId,
      projectName: weld.projectName || '',
      statusByInstantaneous: weld.statusByInstantaneous || 'NULL',
      statusByAvgZone: weld.statusByAvgZone || 'NULL',
      statusByAvgPass: weld.statusByAvgPass || 'NULL',
      statusByAvgTilt: weld.statusByAvgTilt || 'NULL',
      gps: weld.gps,
      minDistance: weld.minDistance || 0,
      numOfGpsPoints: weld.numOfGpsPoints || 0,
      passes: weld.passes || [],
      isFileUpload: weld.isFileUpload === true || weld.is_fileUpload === true,
    };
    
    setSelectedWeldData(weldData);
    setIsWeldDetailsModalOpen(true);
  };

  // Process weld data for map display - ONLY from existing Redux store
  const processedWeldData = useMemo(() => {
    if (!weldData || weldData.length === 0) {
      return [];
    }
    
    // Count welds with and without GPS data
    let weldsWithGps = 0;
    let weldsWithoutGps = 0;
    
    const validWeldData = weldData
      .filter((weld: any) => {
        // Only include welds with valid GPS data
        if (!weld.gps || !weld.gps.Latitude || !weld.gps.Longitude) {
          weldsWithoutGps++;
          return false;
        }
        weldsWithGps++;
        return true;
      })
      .map((weld: any, index: number) => {
        // Parse GPS coordinates
        const latitude = parseFloat(weld.gps.Latitude);
        const longitude = parseFloat(weld.gps.Longitude);
        
        if (isNaN(latitude) || isNaN(longitude)) {
          return null;
        }

        // Determine status color based on statusByInstantaneous (same logic as weld main table)
        let statusColor = 'gray';
        let status = 'Not Started';
        
        if (weld.statusByInstantaneous === 'TRUE') {
          statusColor = 'green';
          status = 'Completed';
        } else if (weld.statusByInstantaneous === 'FALSE') {
          statusColor = 'red';
          status = 'Rejected';
        } else if (weld.statusByInstantaneous === 'EX') {
          statusColor = 'yellow';
          status = 'Exception';
        } else if (weld.statusByInstantaneous === 'CROSS') {
          statusColor = 'violet';
          status = 'Cross';
        } else if (weld.statusByInstantaneous === 'NULL') {
          statusColor = 'gray';
          status = 'Not Started';
        }

        return {
          id: weld.id,
          weldNumber: weld.weldNumber,
          weldIdentifier: weld.weldIdentifier,
          status: status, // Use the determined status
          weldTime: weld.weldTime,
          startTime: weld.startTime,
          endTime: weld.endTime,
          projectId: weld.projectId,
          projectName: weld.projectName,
          jobNumber: weld.job_number,
          gps: {
            lat: latitude,
            lng: longitude,
            HDOP: weld.gps.HDOP,
            Confidence: weld.gps.Confidence
          },
          minDistance: weld.minDistance,
          numOfGpsPoints: weld.numOfGpsPoints,
          statusColor: statusColor
        };
      })
      .filter(Boolean); // Remove null entries

    return validWeldData;
  }, [weldData]);

  // Set up global function for weld details button clicks
  useEffect(() => {
    if (!processedWeldData.length) return;

    window.handleWeldDetailsClick = (id: string, weldNumber: string) => {
      const item = processedWeldData.find((data: any) => data.id.toString() === id);
      if (item) {
        handleOpenWeldPasses(item);
      }
    };

    return () => {
      (window as any).handleWeldDetailsClick = undefined;
    };
  }, [processedWeldData]);

  // Toggle between 2D and 3D view
  const toggle3DView = () => {
    
    if (!map.current) {
      return;
    }
    
    if (!is3DView) {
      // Switch to 3D view
      map.current.easeTo({ 
        pitch: 60, 
        bearing: -20, 
        duration: 1000 
      });
    } else {
      // Switch back to 2D view
      map.current.easeTo({ 
        pitch: 0, 
        bearing: 0, 
        duration: 1000 
      });
    }
    
    setIs3DView(!is3DView);
  };

  // Initialize map - ONLY once
  useEffect(() => {
    
    if (map.current || !mapContainer.current) {
      return;
    }

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/standard",
      center: [-95.519142, 29.945469], // Default center
      zoom: 17,
      attributionControl: false,
      pitch: 0, // Start with no pitch (flat view)
      bearing: 0, // Start with north at the top
      antialias: true, // Enable antialiasing for smoother 3D
    });

    // Add navigation controls
    const nav = new mapboxgl.NavigationControl();
    map.current.addControl(nav, "bottom-right");
    

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []); // Empty dependency array - only run once

  // Add markers when processed weld data changes - ONLY from Redux store
  useEffect(() => {
    if (!map.current || !processedWeldData.length) {
      return;
    }

    // Wait for map to load
    const addMarkers = () => {
      if (!map.current) return;
      
      try {
        // Clear existing markers
        const existingMarkers = document.querySelectorAll('.mapboxgl-marker');
        existingMarkers.forEach(marker => marker.remove());

        // Add new markers from Redux store data
        let validMarkersCount = 0;
        processedWeldData.forEach((weld: any, index: number) => {
          if (!weld.gps || !weld.gps.lat || !weld.gps.lng) {
            return;
          }
          validMarkersCount++;

          const popupHTML = `<div style="padding: 12px; min-width: 320px;">
            <div style="margin-bottom: 16px;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <strong style="font-size: 16px; color: #374151;">Weld ${weld.weldNumber}</strong>
                ${is3DView ? '<span style="font-size: 12px; background: #6366f1; color: white; padding: 2px 8px; border-radius: 4px;">3D</span>' : ''}
              </div>
            </div>
            
            <div style="margin-bottom: 8px;">
              <span style="font-size: 12px; color: #6B7280;">Weld Identifier:</span>
              <span style="font-size: 12px; font-weight: 500; margin-left: 8px; color: #374151;">
                ${weld.weldIdentifier || '-'}
              </span>
            </div>
            
            <div style="margin-bottom: 8px;">
              <span style="font-size: 12px; color: #6B7280;">Status:</span>
              <span style="font-size: 12px; font-weight: 500; margin-left: 8px; color: #374151;">${weld.status}</span>
            </div>
            
            <div style="margin-bottom: 8px;">
              <span style="font-size: 12px; color: #6B7280;">Start Date:</span>
              <span style="font-size: 12px; font-weight: 500; margin-left: 8px; color: #374151;">
                ${(() => {
                  try {
                    if (weld.startTime) {
                      const formatted = formatTimestamp(Number(weld.startTime));
                      return formatted;
                    }
                    return '-';
                  } catch (error) {
                    return 'Error';
                  }
                })()}
              </span>
            </div>
            
            <div style="margin-bottom: 8px;">
              <span style="font-size: 12px; color: #6B7280;">End Date:</span>
              <span style="font-size: 12px; font-weight: 500; margin-left: 8px; color: #374151;">
                ${(() => {
                  try {
                    if (weld.endTime) {
                      const formatted = formatTimestamp(Number(weld.endTime));
                      return formatted;
                    }
                    return '-';
                  } catch (error) {
                    return 'Error';
                  }
                })()}
              </span>
            </div>
            
            <div style="margin-bottom: 8px;">
              <span style="font-size: 12px; color: #6B7280;">Weld Duration:</span>
              <span style="font-size: 12px; font-weight: 500; margin-left: 8px; color: #374151;">
                ${(() => {
                  try {
                    if (weld.weldTime) {
                      const formatted = formatDuration(weld.weldTime);
                      return formatted;
                    }
                    return '-';
                  } catch (error) {
                    return 'Error';
                  }
                })()}
              </span>
            </div>
            
            ${weld.job_number && weld.job_number.length > 0 ? `
            <div style="margin-bottom: 8px;">
              <span style="font-size: 12px; color: #6B7280;">Job Number:</span>
              <span style="font-size: 12px; font-weight: 500; margin-left: 8px; color: #374151;">
                ${Array.isArray(weld.job_number) ? weld.job_number.join(', ') : weld.job_number}
              </span>
            </div>
            ` : ''}
            
            <div style="margin-top: 16px; display: flex; gap: 8px; justify-content: flex-end;">
              <button 
                id="details-btn-${weld.id}" 
                style="
                  background-color: #EB1C37; 
                  color: white; 
                  border: none; 
                  padding: 6px 12px; 
                  border-radius: 8px; 
                  font-size: 12px; 
                  font-weight: 600; 
                  cursor: pointer;
                  transition: all 0.3s;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  min-width: 80px;
                  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                "
                onmouseover="this.style.backgroundColor='#d13d2a'; this.style.boxShadow='0 2px 6px rgba(0, 0, 0, 0.15)';"
                onmouseout="this.style.backgroundColor='#EB1C37'; this.style.boxShadow='0 1px 3px rgba(0, 0, 0, 0.1)';"
                onclick="window.handleWeldDetailsClick('${weld.id}', '${weld.weldNumber}')"
                title="View Details"
              >
                Details
              </button>
              <button 
                id="close-popup-btn-${weld.id}" 
                style="
                  background-color: white; 
                  color: #374151; 
                  border: 1px solid #d1d5db; 
                  padding: 6px 12px; 
                  border-radius: 8px; 
                  font-size: 12px; 
                  font-weight: 600; 
                  cursor: pointer;
                  transition: all 0.3s;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  min-width: 80px;
                  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                "
                onmouseover="this.style.backgroundColor='#f3f4f6'; this.style.borderColor='#9ca3af'; this.style.transform='scale(1.05)'; this.style.boxShadow='0 4px 8px rgba(0, 0, 0, 0.2)';"
                onmouseout="this.style.backgroundColor='white'; this.style.borderColor='#d1d5db'; this.style.transform='scale(1)'; this.style.boxShadow='0 2px 4px rgba(0, 0, 0, 0.1)';"
                onclick="this.closest('.mapboxgl-popup').remove()"
                title="Close"
              >
                Close
              </button>
            </div>
          </div>`;

          const popup = new mapboxgl.Popup({
            offset: 25,
            anchor: "left",
            closeOnClick: false,
          }).setHTML(popupHTML);

          if (map.current) {
            new mapboxgl.Marker({
              color: weld.statusColor || 'blue',
            })
              .setLngLat([weld.gps.lng, weld.gps.lat])
              .setPopup(popup)
              .addTo(map.current);
          }
        });

        // Fit map to markers if we have valid data
        if (validMarkersCount > 0 && map.current) {
          const bounds = new mapboxgl.LngLatBounds();
          processedWeldData.forEach((weld: any) => {
            if (weld.gps && weld.gps.lat && weld.gps.lng) {
              bounds.extend([weld.gps.lng, weld.gps.lat]);
            }
          });
          
          // Adjust pitch and bearing for 3D view
          const options = { 
            padding: 50,
            pitch: is3DView ? 45 : 0, // Add pitch in 3D mode
            bearing: is3DView ? -17.6 : 0 // Slight rotation for better 3D view
          };
          
          map.current.fitBounds(bounds, options);
        }

      } catch (error) {
        // Handle marker addition errors silently
      }
    };

    // Wait for map to load before adding markers
    if (map.current.loaded()) {
      addMarkers();
    } else {
      map.current.on('load', addMarkers);
    }

  }, [processedWeldData, is3DView]); // Depend on processedWeldData and is3DView

  // Always render the main container with 3D button, regardless of data state
  const renderContent = () => {
    // Show loading state - ONLY from Redux store
    if (loading) {
      return (
        <div style={{ 
          height: '100vh', 
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f3f4f6'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', marginBottom: '8px' }}>Loading weld data from Redux store...</div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              {weldData && weldData.length > 0 
                ? `Found ${weldData.length} entries in Redux store, processing for GPS data...`
                : 'Waiting for weld data to load into Redux store...'
              }
            </div>
          </div>
        </div>
      );
    }

    // Show error state - ONLY from Redux store
    if (error) {
      return (
        <div style={{ 
          height: '100vh', 
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fef2f2'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', marginBottom: '8px', color: '#dc2626' }}>Error loading weld data from Redux store</div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>{error}</div>
          </div>
        </div>
      );
    }

    // Show no data state - ONLY from Redux store
    if (!processedWeldData || processedWeldData.length === 0) {
      return (
        <div style={{ 
          height: '100vh', 
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f3f4f6'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', marginBottom: '8px' }}>No GPS data available from Redux store</div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              {weldData && weldData.length > 0 
                ? `Found ${weldData.length} total welds in Redux store, but none have valid GPS coordinates. This is normal if GPS data collection is not enabled for this project.`
                : 'No weld data available in Redux store. Please load a project first.'
              }
            </div>
          </div>
        </div>
      );
    }

    // Show map with data
    return (
      <>
        <div
          ref={mapContainer}
          style={{ height: "100%", width: "100%" }}
        />
        
        {/* Stats Overlay - ONLY from Redux store */}
        {processedWeldData.length > 0 && (
          <div style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            background: "white",
            padding: "16px",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            zIndex: 1000,
            minWidth: "250px"
          }}>
            <h3 style={{ margin: "0 0 12px 0", fontSize: "16px", fontWeight: "600" }}>
              Weld GPS Summary (Redux Store Only)
            </h3>
            <div style={{ fontSize: "14px", lineHeight: "1.5" }}>
              <div><strong>Total in Redux Store:</strong> {weldData?.length || 0}</div>
              <div><strong>Welds with GPS:</strong> {processedWeldData.length}</div>
              <div><strong>GPS Coverage:</strong> {weldData?.length ? `${((processedWeldData.length / weldData.length) * 100).toFixed(1)}%` : '0%'}</div>
              <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '8px', paddingTop: '8px' }}>
                <div><strong>Completed:</strong> {processedWeldData.filter((w: any) => w.status === 'Completed').length}</div>
                <div><strong>In Progress:</strong> {processedWeldData.filter((w: any) => w.status === 'In Progress').length}</div>
                <div><strong>Rejected:</strong> {processedWeldData.filter((w: any) => w.status === 'Rejected').length}</div>
              </div>
              <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '8px', paddingTop: '8px' }}>
                <div><strong>Total GPS Points:</strong> {processedWeldData.reduce((sum: number, weld: any) => sum + (weld.numOfGpsPoints || 0), 0)}</div>
                {currentProject && <div><strong>Project:</strong> {currentProject.project?.projectName || currentProject.project?.projectNumber || '-'}</div>}
              </div>
              <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '8px', paddingTop: '8px' }}>
                <div><strong>View Mode:</strong> {is3DView ? '3D' : '2D'}</div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };


  return (
    <div style={{ height: "100vh", width: "100%", position: "relative" }}>
      {/* Test Button - Always visible */}
      <div style={{
        position: "absolute",
        top: "20px",
        left: "20px",
        zIndex: 1002,
        background: "red",
        color: "white",
        padding: "8px 12px",
        borderRadius: "4px",
        fontSize: "12px"
      }}>
        TEST BUTTON
      </div>

      {/* 3D Toggle Button - Always visible */}
      <div 
        style={{
          position: "absolute",
          top: "60px",
          left: "20px",
          zIndex: 1001,
          background: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          overflow: "hidden",
          border: "1px solid #e5e7eb"
        }}
        onMouseEnter={() => {
          // 3D button container hovered
        }}
      >
        <button
          onClick={() => {
            toggle3DView();
          }}
          style={{
            padding: "12px 16px",
            border: "none",
            background: is3DView ? "#3b82f6" : "white",
            color: is3DView ? "white" : "#374151",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "14px",
            minWidth: "100px",
            justifyContent: "center"
          }}
          onMouseOver={(e) => {
            if (!is3DView) {
              e.currentTarget.style.background = "#f3f4f6";
            }
          }}
          onMouseOut={(e) => {
            if (!is3DView) {
              e.currentTarget.style.background = "white";
            }
          }}
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M21 7.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7.5" />
            <path d="M21 7.5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2" />
            <path d="M12 12a2 2 0 0 0 0-4" />
            <path d="M3 7.5h18" />
          </svg>
          {is3DView ? '3D View' : '2D View'}
        </button>
      </div>

      {/* Render content based on data state */}
      {renderContent()}

      {/* Weld Details Modal */}
      {selectedWeldData && (
        <WeldPassList
          isOpen={isWeldDetailsModalOpen}
          onClose={() => {
            setIsWeldDetailsModalOpen(false);
            setSelectedWeldData(null);
          }}
          weld={selectedWeldData}
          assemblyNumber={`Weld-${selectedWeldData.weldNumber}`}
          statusCalculationMethod={statusCalculationMethod}
          passViewStatusMethod={statusCalculationMethod}
          zoneViewStatusMethod={statusCalculationMethod}
          tiltViewStatusMethod={statusCalculationMethod}
        />
      )}
    </div>
  );
};

export default MapView;